// FILE: src/services/achievementService.js
//
// Phase E1 — Achievement firing + retrieval.
//
// Per the hybrid RLS model (PLAN-phase-a-design.md §6.11.2), achievements
// are TRUSTED BUSINESS EVENTS: all writes flow through the
// record_achievement SECURITY DEFINER RPC. The client never inserts
// directly. The RPC is idempotent: re-firing a key the learner already
// has is a no-op and returns NULL.
//
// Firing helpers in this file are CALL THE RPC + log. The decision logic
// for "should this fire" lives in the caller (sessionService for
// session-driven achievements; readinessService for readiness-driven
// ones; record_certification_outcome itself for certification_earned).

import { supabase } from './supabaseClient';

export const ACHIEVEMENT = {
  BASELINE_SET:         'baseline_set',
  FIRST_FULL_SIM:       'first_full_sim',
  THREE_SIMS:           'three_sims',
  DOMAIN_CLIMBER:       'domain_climber',
  STEADY_PACE:          'steady_pace',
  COMEBACK:             'comeback',
  EXAM_READY_REACHED:   'exam_ready_reached',
  CERTIFICATION_EARNED: 'certification_earned',
  RESILIENCE:           'resilience',
  FOUNDING_TESTER:      'founding_tester',
};

// ── Firing — call the RPC ───────────────────────────────────────────────────

/**
 * Fire an achievement. Idempotent (RPC dedupes via UNIQUE constraint).
 *
 * @param {object} params
 * @param {string} params.key        — must be in achievement_definitions
 * @param {string} [params.examId]   — null for exam-agnostic
 * @param {object} [params.context]  — JSONB metadata (session_id, score, etc.)
 * @returns {Promise<{ id: string|null, earned: boolean, error?: string }>}
 *          id is non-null only when newly earned. earned reflects whether
 *          this call resulted in a new row.
 */
export async function fireAchievement({ key, examId = null, context = null }) {
  if (!key) return { id: null, earned: false, error: 'key is required' };
  try {
    const { data, error } = await supabase.rpc('record_achievement', {
      p_key:     key,
      p_exam_id: examId,
      p_context: context,
    });
    if (error) {
      console.warn('[achievementService] RPC failed for', key, '—', error.message);
      return { id: null, earned: false, error: error.message };
    }
    const earned = !!data;
    if (earned) {
      console.info('[achievementService] earned:', key, examId || '(any)');
      // Phase E1 also dispatches a client-side event so UI surfaces can
      // pop a toast. Phase E3 wires the toast component itself; for now
      // any listener can hook in.
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        try {
          window.dispatchEvent(new CustomEvent('pm-pixel:achievement-earned', {
            detail: { key, examId, id: data },
          }));
        } catch (_) { /* CustomEvent unavailable — non-fatal */ }
      }
    }
    return { id: data || null, earned };
  } catch (err) {
    console.warn('[achievementService] unexpected error:', err?.message || err);
    return { id: null, earned: false, error: err?.message || String(err) };
  }
}

// ── Retrieval ───────────────────────────────────────────────────────────────

/**
 * Load the catalog of all defined achievements (read by anyone).
 */
export async function loadAchievementCatalog() {
  try {
    const { data, error } = await supabase
      .from('achievement_definitions')
      .select('*');
    if (error) {
      console.warn('[achievementService] catalog load failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[achievementService] catalog unexpected error:', err?.message || err);
    return [];
  }
}

/**
 * Load earned achievements for the calling learner. RLS filters to own rows
 * automatically.
 *
 * @returns {Promise<Array<{ id, achievement_key, exam_id, earned_at, context }>>}
 */
export async function loadEarnedAchievements({ limit = 50 } = {}) {
  try {
    const { data, error } = await supabase
      .from('learner_achievements')
      .select('*')
      .order('earned_at', { ascending: false })
      .limit(limit);
    if (error) {
      console.warn('[achievementService] earned load failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[achievementService] earned unexpected error:', err?.message || err);
    return [];
  }
}

/**
 * Bundled call for the "Recent Milestones" strip in the Journey overlay.
 * Returns the catalog joined with the learner's earned rows so the UI
 * can render "recent earned" alongside "next to earn" without two extra
 * passes.
 *
 * @returns {Promise<{ catalog: object[], earned: object[], byKey: object }>}
 */
export async function loadMilestonesSummary() {
  const [catalog, earned] = await Promise.all([
    loadAchievementCatalog(),
    loadEarnedAchievements({ limit: 50 }),
  ]);
  const byKey = {};
  for (const def of catalog) byKey[def.key] = { ...def, earned: false, earned_at: null, context: null };
  for (const row of earned) {
    if (byKey[row.achievement_key]) {
      byKey[row.achievement_key].earned    = true;
      byKey[row.achievement_key].earned_at = row.earned_at;
      byKey[row.achievement_key].context   = row.context;
    }
  }
  return { catalog, earned, byKey };
}

// ── Decision helpers (pure) ─────────────────────────────────────────────────

/**
 * Decide which session-driven achievements should fire after a session
 * completes. Caller invokes the firings via fireAchievement().
 *
 * @param {object} params
 * @param {object} params.session     — { mode, examId, ... }
 * @param {object} params.results     — computeResults() shape
 * @param {number} params.fullSimsToDate — count INCLUDING the just-completed session
 * @returns {Array<{ key, examId, context }>} achievements to fire
 */
export function sessionAchievementsToFire({ session, results, fullSimsToDate }) {
  const out = [];
  const examId = session?.examId || null;
  // first_full_sim and three_sims are exam-agnostic in §6.2 catalog.
  if (session?.mode === 'full' && fullSimsToDate === 1) {
    out.push({ key: ACHIEVEMENT.FIRST_FULL_SIM, examId: null,
      context: { exam_id: examId, score_percentage: results?.percentage } });
  }
  if (session?.mode === 'full' && fullSimsToDate === 3) {
    out.push({ key: ACHIEVEMENT.THREE_SIMS, examId: null,
      context: { exam_id: examId } });
  }
  return out;
}

/**
 * Decide whether the exam_ready_reached achievement should fire based on
 * a readiness compute result transitioning into the exam_ready bucket
 * for the first time.
 *
 * @param {object} params
 * @param {string} params.status            — current readiness status
 * @param {string} [params.previousStatus]  — last snapshot's status if known
 * @param {string} params.examId
 */
export function readinessAchievementsToFire({ status, previousStatus, examId }) {
  const out = [];
  if (status === 'exam_ready' && previousStatus !== 'exam_ready') {
    out.push({
      key: ACHIEVEMENT.EXAM_READY_REACHED,
      examId,
      context: { from_status: previousStatus || 'unknown' },
    });
  }
  return out;
}
